import { api } from '../../lib/apiClient';
import { useAuthStore } from '../../store/authStore';

export const chatKeys = {
  thread: (targetType, targetId) => ['chat-thread', targetType, targetId],
  messages: (threadId) => ['chat-messages', threadId],
  counts: (targetType, targetIds) => ['chat-counts', targetType, targetIds],
};

/** Base path for the active workspace (same pattern as commentsApi). */
function ws() {
  const workspaceId = useAuthStore.getState().workspaceId;
  if (!workspaceId) throw new Error('No active workspace selected');
  return `/workspaces/${workspaceId}`;
}

/** Get-or-create the chat thread attached to a target (file, folder, …). */
export async function resolveChatThread(targetType, targetId) {
  const { data } = await api.post(`${ws()}/chat/threads/resolve`, { targetType, targetId });
  return data; // { id, messageCount, lastMessageAt, isLocked, ... }
}

/** One newest-first page: { data: ChatMessageView[], meta: { page, hasNextPage, ... } }. */
export async function listChatMessages(threadId, { page = 1, limit = 30 } = {}) {
  const { data } = await api.get(`${ws()}/chat/threads/${threadId}/messages`, {
    params: { page, limit },
  });
  return data;
}

/** Post a message; returns it hydrated (the server also pushes it to the room). */
export async function postChatMessage(threadId, body) {
  const { data } = await api.post(`${ws()}/chat/threads/${threadId}/messages`, { body });
  return data;
}

/** Per-target chat counts for a batch of ids → { [targetId]: { threadId, total, unread } }. */
export async function chatCounts(targetType, targetIds) {
  if (!targetIds.length) return {};
  const { data } = await api.get(`${ws()}/chat/threads/counts`, {
    params: { targetType, ids: targetIds.join(',') },
  });
  return data;
}

/** Advance my read cursor for the thread. */
export const markThreadRead = (threadId, lastReadMessageId) =>
  api.post(`${ws()}/chat/threads/${threadId}/read`, lastReadMessageId ? { lastReadMessageId } : {});

/**
 * Prepend a message into page 1 of the infinite messages cache, deduped by id.
 * Both delivery paths funnel through here — the send-mutation success and the
 * socket push — so a message can never render twice regardless of ordering.
 */
export function insertChatMessage(queryClient, threadId, message) {
  queryClient.setQueryData(chatKeys.messages(threadId), (old) => {
    if (!old?.pages?.length) return old; // no cache yet — the initial fetch will include it
    if (old.pages.some((p) => p.data.some((m) => m.id === message.id))) return old;
    const [first, ...rest] = old.pages;
    return { ...old, pages: [{ ...first, data: [message, ...first.data] }, ...rest] };
  });
}

export const setConversationDecision = async (id, decision) => (await api.patch(`${ws()}/chat/threads/${id}/decision`, { decision })).data;
